/**
 * CustomerSummaryCards - 고객 KPI 요약 카드
 *
 * 전체 고객, 즐겨찾기, 이번달 신규, 휴면 고객 카드
 */

import { Users, Star, UserPlus, UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CustomerSegment } from "@/hooks/useCustomerFilters";

interface SummaryCard {
  key: CustomerSegment;
  label: string;
  icon: React.ElementType;
  color: string;
  activeColor: string;
  getValue: (stats: CustomerSummaryStats) => number;
}

export interface CustomerSummaryStats {
  totalElements: number;
  starredCount: number;
  newThisMonthCount: number;
  dormantCount: number;
}

const CARDS: SummaryCard[] = [
  {
    key: "all",
    label: "전체 고객",
    icon: Users,
    color: "text-info",
    activeColor: "bg-info/10 border-info/30 ring-1 ring-info/30",
    getValue: (s) => s.totalElements,
  },
  {
    key: "starred",
    label: "즐겨찾기",
    icon: Star,
    color: "text-amber-600",
    activeColor: "bg-amber-50 border-amber-200 ring-1 ring-amber-200",
    getValue: (s) => s.starredCount,
  },
  {
    key: "newThisMonth",
    label: "이번달 신규",
    icon: UserPlus,
    color: "text-success",
    activeColor: "bg-success/10 border-success/30 ring-1 ring-success/30",
    getValue: (s) => s.newThisMonthCount,
  },
  {
    key: "dormant",
    label: "휴면 고객",
    icon: UserX,
    color: "text-destructive",
    activeColor: "bg-destructive/10 border-destructive/30 ring-1 ring-destructive/30",
    getValue: (s) => s.dormantCount,
  },
];

interface CustomerSummaryCardsProps {
  stats: CustomerSummaryStats;
  activeSegment: CustomerSegment;
  onCardClick: (segment: CustomerSegment) => void;
}

export function CustomerSummaryCards({
  stats,
  activeSegment,
  onCardClick,
}: CustomerSummaryCardsProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 py-3">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const isActive = activeSegment === card.key;
        const value = card.getValue(stats);

        return (
          <button
            key={card.key}
            onClick={() => onCardClick(isActive ? null : card.key)}
            className={cn(
              "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
              "hover:shadow-sm",
              isActive ? card.activeColor : "bg-background hover:bg-muted/50",
            )}
          >
            <div
              className={cn(
                "flex items-center justify-center w-9 h-9 rounded-lg",
                isActive ? "bg-white/80" : "bg-muted",
              )}
            >
              <Icon className={cn("w-4.5 h-4.5", card.color)} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground truncate">
                {card.label}
              </p>
              <p className="text-lg font-bold leading-tight">
                {value.toLocaleString()}
                <span className="text-xs font-normal text-muted-foreground ml-0.5">
                  명
                </span>
              </p>
            </div>
          </button>
        );
      })}
    </div>
  );
}
